import { Injectable, inject } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  EMPTY,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';

import { CsrfTokenDtoApiModel } from '../../api';
import { CSRFApiService } from '../../api/api/cSRF.api.service';
import { LoggerService } from '../../core/logging';

@Injectable({
  providedIn: 'root',
})
export class CsrfTokenService {
  private readonly csrfToken$: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);
  private readonly logger = inject(LoggerService);

  constructor(private readonly csrfApiService: CSRFApiService) {}

  public get csrfToken(): Observable<string | null> {
    return this.csrfToken$;
  }

  public clearToken(): void {
    this.csrfToken$.next(null);
  }

  public getCsrfToken(): Observable<void> {
    if (this.csrfToken$.value) {
      return of(void 0);
    }

    return this.csrfApiService.csrfControllerGetCsrfToken().pipe(
      tap((token: CsrfTokenDtoApiModel) => {
        this.csrfToken$.next(token.csrfToken);
      }),
      catchError((error: unknown) => {
        this.logger.error('Failed to fetch CSRF token', error, {
          module: 'CsrfTokenService',
        });
        return EMPTY;
      }),
      map(() => void 0),
    );
  }

  public getNewCsrfToken(): Observable<void> {
    return this.csrfApiService.csrfControllerGetNewCsrfToken().pipe(
      tap((token: CsrfTokenDtoApiModel) => {
        this.csrfToken$.next(token.csrfToken);
      }),
      catchError((error: unknown) => {
        this.logger.error('Failed to fetch new CSRF token', error, {
          module: 'CsrfTokenService',
        });
        return EMPTY;
      }),
      map(() => void 0),
    );
  }

  // Methode für einen "sicheren" CSRF-Token (mit Caching)
  public getSafeCsrfToken(): Observable<void> {
    if (this.csrfToken$.value) {
      return of(void 0);
    }

    return this.csrfApiService.csrfControllerGetSafeCsrfToken().pipe(
      tap((token: CsrfTokenDtoApiModel) => {
        this.csrfToken$.next(token.csrfToken);
      }),
      catchError(() => {
        return EMPTY;
      }),
      map(() => void 0),
    );
  }
}
